import React, { FC, InputHTMLAttributes } from 'react';
import { Preamp } from '@red-digital/bricks';
import { Product, SelectedProductsState, Feature, Modals, PlanCardsMetadata } from '~/types/products';
import QuizPlanCards from './quiz';
import NewPlanCards from './new';
import DefaultPlanCards from './default';
import { ReactSelectOption } from '~/types/common';
import ContinueToVAS from './ContinueToVAS';
import { Card } from '~/types/preamp';
import ToggleSwitch from './ToggleSwitch';
import { NETFLIX_BUNDLE } from '~/constants/entityIds';

interface Props {
  metadata?: PlanCardsMetadata;
  activeProducts: Product[];
  addPhoneState: boolean;
  bundleCost: number;
  bundleFeatures: Feature[];
  carousel: {
    direction: number;
    productIndex: number;
    paginate: (index: number) => void;
  };
  sweetener: {
    label: string;
    value: string;
  };
  singleProduct: boolean;
  is500: boolean;
  is1Gig: boolean;
  is2Gig: boolean;
  is5Gig: boolean;
  is7Gig: boolean;
  is200: boolean;
  isCopper: boolean;
  handleSelectProduct: (product: Product, redirect?: boolean) => void;
  handleSelectPlan?: (plan: Product, forceScroll?: boolean) => void;
  setActiveModal: (key: Modals) => void;
  setEeroModal: (product: Product) => void;
  setVasModal: (product: Product) => void;
  setBroadbandConsumerLabelModal: (product: Product) => void;
  selectedState?: SelectedProductsState;
  isCopper2Fiber: boolean;
  card?: Card;
  recommended?: string;
  productFilters?: {
    options: ReactSelectOption[];
    setOptions: (options: ReactSelectOption[]) => void;
    sweetener: ReactSelectOption;
    setSweetener: (selected: ReactSelectOption) => void;
    voiceAvailable: boolean;
    handleAddHomePhone: (event: InputHTMLAttributes<HTMLInputElement>) => void;
    handleAddProductState: (product: Product, option?: Product) => void;
    addPhoneState: boolean;
  };
  clickToRevealVas?: boolean;
  setClickToRevealVas?: (value: boolean) => void;
  landingPageProduct?: string;
  ndiBanner?: boolean;
  clickToToggleNetflixBundle?: boolean;
  setClickToToggleNetflixBundle?: (value: boolean) => void;
  showPlanCardBanner: boolean;
  netflixToggleEnabled?: string;
  netflixToggleDefault?: string;
  videoProducts?: Product[];
  isQuizSession?: boolean;
}

const PlanCards: FC<Props> = (props) => {
  const redesignedCards = !props?.singleProduct && !props?.isCopper && !props?.isCopper2Fiber;
  const Component = props.recommended ? QuizPlanCards : redesignedCards ? NewPlanCards : DefaultPlanCards;
  const netflixVasAvailable = props?.videoProducts?.some((product) => product?.productId === NETFLIX_BUNDLE);

  // Handle product selection on landing pages
  const continueToVas = () => {
    props?.setClickToRevealVas(false);
    // Passing forceScroll = true because setting clickToRevealVas above doesn't necessarily update the store in time for this function call
    props?.handleSelectPlan(props?.selectedState?.internet, true);
  };

  return (
    <>
      {/* Netflix ToggleSwitch */}
      {props?.netflixToggleEnabled !== 'false' && netflixVasAvailable && !props?.isCopper && !props?.isQuizSession && (
        <ToggleSwitch
          isCopper={props?.isCopper}
          clickToToggleNetflixBundle={props?.clickToToggleNetflixBundle}
          setClickToToggleNetflixBundle={props?.setClickToToggleNetflixBundle}
          netflixToggleDefault={props?.netflixToggleDefault}
        />
      )}

      {/* Plan Cards */}
      <Preamp placement="4xPj6N8UVVzt18dXyTpo8j">{(metadata) => <Component {...{ ...metadata, ...props }} />}</Preamp>

      {/* Continue to VAS CTA (Landing Page Product Selection) */}
      {props?.clickToRevealVas && <ContinueToVAS clickHandler={continueToVas} ndiBanner={props?.ndiBanner} />}
    </>
  );
};

PlanCards.displayName = 'PlanCards';

export default PlanCards;
